import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { API_CONFIG } from '../../config/api';
import type { RootState } from '../store';
import type { Order, OrderSummary, Address, CheckoutRequest, CancelOrderRequest, CreateAddressRequest, OrderStatus } from '../../types/Order';
import { getCartToken } from '../../lib/utils/cartToken';
import { readApiResponse } from '../../lib/utils/apiResponse';
import { logout, sessionExpired } from './authSlice';

interface OrdersResponse {
  orders: OrderSummary[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

interface OrderState {
  orders: OrderSummary[];
  currentOrder: Order | null;
  addresses: Address[];
  orderStatuses: OrderStatus[];
  pagination: {
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
  };
  isLoading: boolean;
  isLoadingMore: boolean;
  isCheckingOut: boolean;
  error: string | null;
}

const initialState: OrderState = {
  orders: [],
  currentOrder: null,
  addresses: [],
  orderStatuses: [],
  pagination: {
    totalCount: 0,
    pageNumber: 1,
    pageSize: 10,
    totalPages: 0,
  },
  isLoading: false,
  isLoadingMore: false,
  isCheckingOut: false,
  error: null,
};

const getAuthHeader = (getState: () => unknown) => {
  const state = getState() as RootState;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (state.auth.accessToken) {
    headers['Authorization'] = `Bearer ${state.auth.accessToken}`;
  } else {
    // Guest checkout: identify the server-side cart by its token.
    headers['X-Cart-Token'] = getCartToken();
  }
  return headers;
};

// Fetch user's orders
export const fetchOrders = createAsyncThunk<OrdersResponse, { page?: number; pageSize?: number }>(
  'orders/fetchOrders',
  async ({ page = 1, pageSize = 10 }, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_CONFIG.API_URL}/order?page=${page}&pageSize=${pageSize}`,
        { headers: getAuthHeader(getState) }
      );
      
      if (response.status === 401) return rejectWithValue('SESSION_EXPIRED');
      
      const data = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.[0] || 'Failed to fetch orders');
      }
      return data.result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch orders');
    }
  }
);

// Fetch more orders (for infinite scroll - appends to existing)
export const fetchMoreOrders = createAsyncThunk<OrdersResponse, { page: number; pageSize?: number }>(
  'orders/fetchMoreOrders',
  async ({ page, pageSize = 10 }, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_CONFIG.API_URL}/order?page=${page}&pageSize=${pageSize}`,
        { headers: getAuthHeader(getState) }
      );
      
      if (response.status === 401) return rejectWithValue('SESSION_EXPIRED');
      
      const data = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.[0] || 'Failed to fetch orders');
      }
      return data.result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch orders');
    }
  }
);

// Fetch single order details
export const fetchOrderById = createAsyncThunk<Order, string>(
  'orders/fetchOrderById',
  async (orderId, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_CONFIG.API_URL}/order/${orderId}`,
        { headers: getAuthHeader(getState) }
      );
      
      if (response.status === 401) return rejectWithValue('SESSION_EXPIRED');
      
      const data = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.[0] || 'Failed to fetch order');
      }
      return data.result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch order');
    }
  }
);

// Checkout
export const checkout = createAsyncThunk<Order, CheckoutRequest>(
  'orders/checkout',
  async (request, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/order/checkout`, {
        method: 'POST',
        headers: getAuthHeader(getState),
        body: JSON.stringify(request),
      });
      
      if (response.status === 401) return rejectWithValue('SESSION_EXPIRED');
      
      const data = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.[0] || 'Checkout failed');
      }
      return data.result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Checkout failed');
    }
  }
);

// Cancel order
export const cancelOrder = createAsyncThunk<void, { orderId: string; request: CancelOrderRequest }>(
  'orders/cancelOrder',
  async ({ orderId, request }, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/order/${orderId}/cancel`, {
        method: 'POST',
        headers: getAuthHeader(getState),
        body: JSON.stringify(request),
      });
      
      if (response.status === 401) return rejectWithValue('SESSION_EXPIRED');
      
      const data = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.[0] || 'Failed to cancel order');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to cancel order');
    }
  }
);

// Fetch user addresses
export const fetchAddresses = createAsyncThunk<Address[], void>(
  'orders/fetchAddresses',
  async (_, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_CONFIG.API_URL}/address`,
        { headers: getAuthHeader(getState) }
      );
      
      if (response.status === 401) return rejectWithValue('SESSION_EXPIRED');
      
      const data = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.[0] || 'Failed to fetch addresses');
      }
      return data.result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch addresses');
    }
  }
);

// Create address
export const createAddress = createAsyncThunk<Address, CreateAddressRequest>(
  'orders/createAddress',
  async (request, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/address`, {
        method: 'POST',
        headers: getAuthHeader(getState),
        body: JSON.stringify(request),
      });
      
      if (response.status === 401) return rejectWithValue('SESSION_EXPIRED');
      
      const data = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.[0] || 'Failed to create address');
      }
      return data.result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create address');
    }
  }
);

// Fetch order statuses
export const fetchOrderStatuses = createAsyncThunk<OrderStatus[], void>(
  'orders/fetchOrderStatuses',
  async (_, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_CONFIG.API_URL}/order/statuses`,
        { headers: getAuthHeader(getState) }
      );
      
      if (response.status === 401) return rejectWithValue('SESSION_EXPIRED');
      
      const data = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.[0] || 'Failed to fetch order statuses');
      }
      return data.result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch order statuses');
    }
  }
);

const orderSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    clearOrderError: (state) => {
      state.error = null;
    },
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch orders
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action: PayloadAction<OrdersResponse>) => {
        state.isLoading = false;
        state.orders = action.payload.orders;
        state.pagination = {
          totalCount: action.payload.totalCount,
          pageNumber: action.payload.pageNumber,
          pageSize: action.payload.pageSize,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch more orders (infinite scroll)
    builder
      .addCase(fetchMoreOrders.pending, (state) => {
        state.isLoadingMore = true;
      })
      .addCase(fetchMoreOrders.fulfilled, (state, action: PayloadAction<OrdersResponse>) => {
        state.isLoadingMore = false;
        state.orders = [...state.orders, ...action.payload.orders];
        state.pagination = {
          totalCount: action.payload.totalCount,
          pageNumber: action.payload.pageNumber,
          pageSize: action.payload.pageSize,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchMoreOrders.rejected, (state, action) => {
        state.isLoadingMore = false;
        state.error = action.payload as string;
      });

    // Fetch order by ID
    builder
      .addCase(fetchOrderById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action: PayloadAction<Order>) => {
        state.isLoading = false;
        state.currentOrder = action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Checkout
    builder
      .addCase(checkout.pending, (state) => {
        state.isCheckingOut = true;
        state.error = null;
      })
      .addCase(checkout.fulfilled, (state, action: PayloadAction<Order>) => {
        state.isCheckingOut = false;
        state.currentOrder = action.payload;
      })
      .addCase(checkout.rejected, (state, action) => {
        state.isCheckingOut = false;
        state.error = action.payload as string;
      });

    // Cancel order
    builder
      .addCase(cancelOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelOrder.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(cancelOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch addresses
    builder
      .addCase(fetchAddresses.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAddresses.fulfilled, (state, action: PayloadAction<Address[]>) => {
        state.isLoading = false;
        state.addresses = action.payload;
      })
      .addCase(fetchAddresses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Create address
    builder
      .addCase(createAddress.fulfilled, (state, action: PayloadAction<Address>) => {
        state.addresses.push(action.payload);
      });

    // Fetch order statuses
    builder
      .addCase(fetchOrderStatuses.fulfilled, (state, action: PayloadAction<OrderStatus[]>) => {
        state.orderStatuses = action.payload;
      });

    // Drop the previous user's orders and saved addresses when they sign out or
    // their session expires, so nothing leaks into the next session.
    // Must be registered after every addCase, per RTK's builder ordering rules.
    builder.addMatcher(
      (action) => action.type === logout.type || action.type === sessionExpired.type,
      () => initialState
    );
  },
});

export const { clearOrderError, clearCurrentOrder } = orderSlice.actions;

export const selectOrders = (state: RootState) => state.orders.orders;
export const selectCurrentOrder = (state: RootState) => state.orders.currentOrder;
export const selectAddresses = (state: RootState) => state.orders.addresses;
export const selectOrderStatuses = (state: RootState) => state.orders.orderStatuses;
export const selectOrdersPagination = (state: RootState) => state.orders.pagination;
export const selectOrdersLoading = (state: RootState) => state.orders.isLoading;
export const selectOrdersLoadingMore = (state: RootState) => state.orders.isLoadingMore;
export const selectIsCheckingOut = (state: RootState) => state.orders.isCheckingOut;
export const selectOrderError = (state: RootState) => state.orders.error;

export default orderSlice.reducer;
